import React from 'react';
import styles from './ConfirmacionModal.module.css';
import Button from '../ui/button';
import Input from '../ui/input';
import { MyIcons } from '../icons/my-icons';

/**
 * Modal de confirmación para honorarios/facturas
 * Props:
 * - isOpen: boolean (si el modal está visible)
 * - onClose: función para cerrar el modal
 * - onConfirm: función para confirmar la acción
 * - message: mensaje de advertencia
 * - alertFields: objeto con campos dinámicos de alerta (ej: {"Facturas ya cobradas": [...], "Facturas no cobradas aún: ": [...]})
 * - facturasYaCobradas: array de facturas ya cobradas (legacy - deprecado, usar alertFields)
 * - facturasNoCobradas: array de facturas no cobradas (legacy - deprecado, usar alertFields)
 * - honorariosYaPagados: array de honorarios ya pagados (legacy - deprecado, usar alertFields)
 * - numFactura: número de factura inicial
 * - fecha: fecha inicial
 * - numFacturaAdmin: número de factura admin (solo para honorarios)
 * - onNumFacturaChange: función para cambiar numFactura
 * - onFechaChange: función para cambiar fecha
 * - onNumFacturaAdminChange: función para cambiar numFacturaAdmin (opcional)
 * - loading: boolean (si está cargando)
 * - showNumFacturaAdmin: boolean (si mostrar campo numFacturaAdmin)
 * - totalImporteFacturas: number (total de importes de facturas cobradas)
 * - totalComisiones: number (total de comisiones de honorarios)
 * - showFecha: boolean (si mostrar campo fecha, default true)
 */
export default function ConfirmacionModal({
  isOpen,
  onClose,
  onConfirm,
  message,
  alertFields,
  // Legacy props for backward compatibility
  facturasYaCobradas,
  facturasNoCobradas,
  honorariosYaPagados,
  numFactura,
  fecha,
  numFacturaAdmin,
  onNumFacturaChange,
  onFechaChange,
  onNumFacturaAdminChange,
  loading,
  showNumFacturaAdmin = false,
  showFecha = true,
  totalImporteFacturas,
  totalComisiones
}) {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
  };

  // Construir objeto de campos de alerta (usar alertFields si existe, o construir desde props legacy)
  const fieldsToDisplay = alertFields || {};
  
  // Backward compatibility: añadir props legacy si no hay alertFields
  if (!alertFields) {
    if (facturasYaCobradas && facturasYaCobradas.length > 0) {
      fieldsToDisplay['Facturas ya cobradas'] = facturasYaCobradas;
    }
    if (facturasNoCobradas && facturasNoCobradas.length > 0) {
      fieldsToDisplay['Facturas no cobradas aún'] = facturasNoCobradas;
    }
    if (honorariosYaPagados && honorariosYaPagados.length > 0) {
      fieldsToDisplay['Honorarios ya pagados'] = honorariosYaPagados;
    }
  }

  console.log('ConfirmacionModal - fieldsToDisplay:', fieldsToDisplay);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Confirmar Operación</h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            type="button"
            disabled={loading}
          >
            <MyIcons name="close" width={20} height={20} color="var(--gray)" />
          </button>
        </div>

        <div className={styles.content}>
          {/* Mensaje de advertencia */}
          <div className={styles.warningSection}>
            <div className={styles.warningIcon}>
              <MyIcons name="alarm" width={24} height={24} color="var(--orange)" />
            </div>
            <p className={styles.warningMessage}>{message}</p>
          </div>

          {/* Renderizar todas las listas de alerta dinámicamente */}
          {Object.keys(fieldsToDisplay).map((fieldName) => {
            const items = fieldsToDisplay[fieldName];
            if (!Array.isArray(items) || items.length === 0) return null;
            
            return (
              <div key={fieldName} className={styles.alertSection}>
                <h3 className={styles.alertTitle}>{fieldName}:</h3>
                <ul className={styles.alertList}>
                  {items.map((item, index) => (
                    <li key={index} className={styles.alertItem}>{item}</li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* Sección de totales */}
          {(totalImporteFacturas || totalComisiones) && (
            <div className={styles.alertSection} style={{ borderLeftColor: 'var(--green)' }}>
              <h3 className={styles.alertTitle}>Totales:</h3>
              <ul className={styles.alertList}>
                {totalImporteFacturas && (
                  <li className={styles.alertItem}>Total Importe Facturas: <strong style={{ color: 'var(--black)' }}>{parseFloat(totalImporteFacturas).toFixed(2)}€</strong></li>
                )}
                {totalComisiones && (
                  <li className={styles.alertItem}>Total Comisiones: <strong style={{ color: 'var(--black)' }}>{parseFloat(totalComisiones).toFixed(2)}€</strong></li>
                )}
              </ul>
            </div>
          )}

          {/* Campos editables */}
          <div className={styles.formSection}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Número(s) de Factura <span className={styles.required}>*</span>
              </label>
              <Input
                type="text"
                value={numFactura}
                onChange={(e) => onNumFacturaChange(e.target.value)}
                placeholder="numFactura1, numFactura2, numFactura3"
                disabled={loading}
                required
              />
            </div>

            {showFecha && (
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Fecha <span className={styles.required}>*</span>
                </label>
                <Input
                  type="date"
                  value={fecha}
                  onChange={(e) => onFechaChange(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            )}

            {showNumFacturaAdmin && (
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Número de Factura Administrador
                </label>
                <Input
                  type="text"
                  value={numFacturaAdmin}
                  onChange={(e) => onNumFacturaAdminChange(e.target.value)}
                  placeholder="Ingresa el número de factura del administrador"
                  disabled={loading}
                />
              </div>
            )}
          </div>
        </div>

        <div className={styles.buttonGroup}>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            CANCELAR
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'CONFIRMANDO...' : 'CONFIRMAR'}
          </Button>
        </div>
      </div>
    </div>
  );
}
